/**
 * Gemeinsame Teile der Anmeldung (Schritte, Fehlersätze, Code-Prüfung, Vertrag des Clients). Eigene Datei, damit die
 * nachgeladene Supabase-Anmeldung (`auth-supabase.ts`) sie nutzen kann, ohne `auth.ts` zu importieren – sonst entstünde
 * ein Kreis, weil `auth.ts` sie nachlädt (AD-1, Regel „kein-kreis“).
 */

/** Wo die Anmeldung gerade steht. */
export type LoginStep =
  | { kind: 'password' }
  /** Passwort stimmt, aber noch kein zweiter Faktor: Authenticator-App einrichten. */
  | { kind: 'enroll'; factorId: string; qrCode: string; secret: string }
  /** Passwort stimmt, Faktor vorhanden: Code aus der App eingeben. */
  | { kind: 'code'; factorId: string }
  /** Angemeldet: bei currentStep mit Passwort (aal1 oder aal2), bei secondFactorStep mit zweitem Faktor (aal2). */
  | { kind: 'done' };

/** Fehler der Anmeldung mit deutschem Satz (Ursache + nächster Schritt). */
export class LoginError extends Error {
  constructor(
    message: string,
    /** Welches Feld betroffen ist, damit die Seite den Fehler dort zeigt. */
    readonly field: 'password' | 'code' | null = null,
  ) {
    super(message);
    this.name = 'LoginError';
  }
}

export const LOGIN_TEXTS = {
  wrongPassword: 'E-Mail oder Passwort stimmen nicht.',
  wrongCode: 'Der Code stimmt nicht. Bitte den aktuellen Code aus der App eingeben.',
  codeFormat: 'Der Code hat 6 Ziffern. Bitte den aktuellen Code aus der App eingeben.',
  tooMany: 'Zu viele Versuche. Bitte warte ein paar Minuten und versuche es dann noch einmal.',
  general: 'Die Anmeldung hat nicht geklappt. Bitte versuche es noch einmal; wenn es wieder passiert, Rudolf ansprechen.',
  offline: 'Der Anmeldedienst ist gerade nicht erreichbar. Prüfe die Internetverbindung und versuche es gleich noch einmal.',
  testMode: 'Im Prüfmodus gibt es keine Anmeldung mit Passwort.',
  /** Gesperrt (D4): gleicher Satz wie die Antwort des ERP-Servers. */
  blocked: 'Dein Zugang ist gesperrt. Bitte Rudolf ansprechen.',
  linkUsed: 'Dieser Link ist schon benutzt oder abgelaufen. Bitte Rudolf ansprechen.',
  linkBroken: 'Dieser Link ist unvollständig. Bitte den ganzen Link aus der Nachricht öffnen oder Rudolf ansprechen.',
  passwordMissing: 'Bitte ein Passwort eingeben.',
  passwordMismatch: 'Die beiden Passwörter sind nicht gleich. Bitte noch einmal eingeben.',
  passwordWeak: 'Das Passwort ist zu kurz oder zu einfach. Bitte ein längeres Passwort wählen, am besten aus dem Passwort-Manager.',
  emailMissing: 'Bitte deine E-Mail-Adresse eingeben.',
  resetSent:
    'Wenn es zu dieser Adresse einen Zugang gibt, kommt gleich eine E-Mail mit einem Link. Damit setzt du ein neues Passwort. Keine E-Mail? Bitte Rudolf ansprechen.',
} as const;

export interface AuthClient {
  /** Ausweis für die ERP-Schnittstelle; ob aal1 reicht, entscheidet der Server per Einstellung. */
  getAccessToken(): Promise<string | null>;
  /** Ohne Sitzung `password`, sonst `done` (mit Passwort angemeldet). */
  currentStep(): Promise<LoginStep>;
  /**
   * Nur wenn der Server den zweiten Faktor verlangt: `done` bei Stufe aal2, sonst Code
   * eingeben oder – ohne Faktor – Authenticator-App einrichten (legt den Faktor an).
   */
  secondFactorStep(): Promise<LoginStep>;
  signInWithPassword(email: string, password: string): Promise<void>;
  /** Prüft den 6-stelligen Code (Einrichten und normale Anmeldung). */
  verifyCode(factorId: string, code: string): Promise<void>;
  /** „Passwort vergessen“: Supabase schickt einen Link an die Adresse (ohne zu verraten, ob es sie gibt). */
  requestPasswordReset(email: string): Promise<void>;
  /** Einrichtungs-Link einlösen (`token_hash` aus dem Fragment); danach mit Passwort angemeldet (aal1). */
  redeemSetupLink(tokenHash: string): Promise<void>;
  /** Bestätigter TOTP-Faktor der angemeldeten Person (dann verlangt Supabase für ein neues Passwort aal2). */
  verifiedFactorId(): Promise<string | null>;
  /** Neues Passwort für die angemeldete Person (nach dem Einlösen des Links). */
  setPassword(password: string): Promise<void>;
  signOut(): Promise<void>;
  /** Meldet nur Abmeldungen von außen (abgelaufen, anderer Tab), nicht jeden Zwischenschritt. */
  onSignedOut(listener: () => void): () => void;
}

/** Code ohne Leerzeichen („123 456“ → „123456“); gemeinsame Bereinigung für Feld und Prüfung. */
export function stripCodeSpaces(input: string): string {
  return input.replace(/\s+/g, '');
}

/** Nur Ziffern, Leerzeichen dürfen mit eingegeben werden („123 456“). */
export function normalizeCode(input: string): string | null {
  const code = stripCodeSpaces(input);
  return /^\d{6}$/.test(code) ? code : null;
}

/**
 * Unter diesem Schlüssel legt Supabase die Sitzung im Browser ab (gleicher Wert wie der Supabase-Standard, damit
 * bestehende Anmeldungen bleiben). Ausdrücklich gesetzt, damit das Abmelden die Sitzung auch dann löschen kann, wenn
 * sich die Supabase-Anmeldung gerade nicht nachladen lässt.
 */
export function supabaseStorageKey(supabaseUrl: string): string {
  return `sb-${new URL(supabaseUrl).hostname.split('.')[0]}-auth-token`;
}

/** Gespeicherte Sitzung dieses Geräts entfernen (Sitzung, PKCE-Prüfwert, Nutzerdaten) – wie `signOut({ scope: 'local' })`. */
export function clearStoredSupabaseSession(storage: Pick<Storage, 'removeItem'>, supabaseUrl: string): void {
  const key = supabaseStorageKey(supabaseUrl);
  for (const k of [key, `${key}-code-verifier`, `${key}-user`]) storage.removeItem(k);
}
